import { useEffect, useRef } from "react";

/**
 * 日落海滩风格背景
 * Canvas 绘制：暖色渐变天空、落日光辉、云层、远山剪影、海面倒影
 * 参考图片色调：深橙 → 珊瑚 → 暮紫的黄昏渐变
 */
export default function GhibliBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationId: number;
    let time = 0;

    function resize() {
      if (!canvas) return;
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      draw();
    }

    function draw() {
      if (!ctx || !canvas) return;
      const w = canvas.width;
      const h = canvas.height;

      // 1. 天空渐变 — 黄昏暖色调
      drawSunsetSky(w, h);

      // 2. 落日光辉
      drawSunGlow(w, h);

      // 3. 云层
      drawSunsetClouds(w, h);

      // 4. 远山剪影
      drawSilhouetteMountains(w, h);

      // 5. 海面 / 地平线
      drawSeaReflection(w, h);

      // 6. 前景沙丘剪影
      drawSandDunes(w, h);

      // 7. 光线粒子
      drawLightParticles(w, h);
    }

    function drawSunsetSky(w: number, h: number) {
      if (!ctx) return;
      const skyGradient = ctx.createLinearGradient(0, 0, 0, h);
      // 顶部深暮紫
      skyGradient.addColorStop(0, "#2D1B4E");
      // 中上淡紫
      skyGradient.addColorStop(0.2, "#5C3D6E");
      // 中部玫瑰粉
      skyGradient.addColorStop(0.4, "#C06070");
      // 中下珊瑚橙
      skyGradient.addColorStop(0.6, "#E8845A");
      // 地平线金橙
      skyGradient.addColorStop(0.8, "#F4A03B");
      // 最底部暖黄
      skyGradient.addColorStop(1, "#FCE38A");
      ctx.fillStyle = skyGradient;
      ctx.fillRect(0, 0, w, h);
    }

    function drawSunGlow(w: number, h: number) {
      if (!ctx) return;
      // 主太阳光辉
      const sunX = w * 0.5;
      const sunY = h * 0.72;

      const sunGlow = ctx.createRadialGradient(sunX, sunY, 0, sunX, sunY, w * 0.35);
      sunGlow.addColorStop(0, "rgba(255, 230, 150, 0.5)");
      sunGlow.addColorStop(0.3, "rgba(255, 180, 80, 0.25)");
      sunGlow.addColorStop(0.6, "rgba(240, 120, 60, 0.1)");
      sunGlow.addColorStop(1, "rgba(200, 80, 50, 0)");
      ctx.fillStyle = sunGlow;
      ctx.fillRect(0, 0, w, h);

      // 太阳核心
      const coreGlow = ctx.createRadialGradient(sunX, sunY, 0, sunX, sunY, w * 0.06);
      coreGlow.addColorStop(0, "rgba(255, 245, 200, 0.7)");
      coreGlow.addColorStop(0.5, "rgba(255, 200, 100, 0.3)");
      coreGlow.addColorStop(1, "rgba(255, 160, 60, 0)");
      ctx.fillStyle = coreGlow;
      ctx.fillRect(0, 0, w, h);
    }

    function drawSunsetClouds(w: number, h: number) {
      if (!ctx) return;
      const clouds = [
        { x: w * 0.12, y: h * 0.15, scaleX: 1.8, scaleY: 0.4, color: "rgba(180, 100, 130, 0.3)" },
        { x: w * 0.35, y: h * 0.22, scaleX: 2.2, scaleY: 0.35, color: "rgba(200, 120, 100, 0.25)" },
        { x: w * 0.65, y: h * 0.18, scaleX: 1.5, scaleY: 0.3, color: "rgba(190, 110, 120, 0.28)" },
        { x: w * 0.85, y: h * 0.12, scaleX: 1.3, scaleY: 0.25, color: "rgba(160, 90, 140, 0.2)" },
        // 低空薄云 — 受落日照亮
        { x: w * 0.25, y: h * 0.50, scaleX: 2.5, scaleY: 0.15, color: "rgba(240, 160, 100, 0.18)" },
        { x: w * 0.70, y: h * 0.45, scaleX: 2.0, scaleY: 0.12, color: "rgba(230, 140, 90, 0.15)" },
      ];

      clouds.forEach((cloud) => {
        ctx.save();
        ctx.translate(cloud.x, cloud.y);
        ctx.scale(cloud.scaleX, cloud.scaleY);

        // 云朵底部暗影
        ctx.fillStyle = cloud.color;
        drawCloudShape(ctx, 0, 4, 1.1);

        // 云朵主体
        ctx.fillStyle = cloud.color.replace(/[\d.]+\)$/, "0.9)");
        drawCloudShape(ctx, 0, 0, 1);

        ctx.restore();
      });
    }

    function drawCloudShape(
      c: CanvasRenderingContext2D,
      offsetX: number,
      offsetY: number,
      scale: number
    ) {
      c.beginPath();
      c.arc(offsetX - 40 * scale, offsetY, 28 * scale, 0, Math.PI * 2);
      c.arc(offsetX - 10 * scale, offsetY - 14 * scale, 38 * scale, 0, Math.PI * 2);
      c.arc(offsetX + 28 * scale, offsetY - 10 * scale, 32 * scale, 0, Math.PI * 2);
      c.arc(offsetX + 55 * scale, offsetY + 4 * scale, 24 * scale, 0, Math.PI * 2);
      c.arc(offsetX + 8 * scale, offsetY + 8 * scale, 28 * scale, 0, Math.PI * 2);
      c.fill();
    }

    function drawSilhouetteMountains(w: number, h: number) {
      if (!ctx) return;
      // 远山 — 深紫蓝剪影
      ctx.fillStyle = "rgba(40, 25, 60, 0.6)";
      ctx.beginPath();
      ctx.moveTo(0, h * 0.68);
      ctx.lineTo(w * 0.08, h * 0.60);
      ctx.lineTo(w * 0.18, h * 0.64);
      ctx.lineTo(w * 0.28, h * 0.56);
      ctx.lineTo(w * 0.40, h * 0.62);
      ctx.lineTo(w * 0.52, h * 0.54);
      ctx.lineTo(w * 0.62, h * 0.58);
      ctx.lineTo(w * 0.75, h * 0.52);
      ctx.lineTo(w * 0.85, h * 0.57);
      ctx.lineTo(w * 0.95, h * 0.55);
      ctx.lineTo(w, h * 0.60);
      ctx.lineTo(w, h * 0.75);
      ctx.lineTo(0, h * 0.75);
      ctx.closePath();
      ctx.fill();

      // 近山 — 更深的剪影
      ctx.fillStyle = "rgba(30, 18, 45, 0.7)";
      ctx.beginPath();
      ctx.moveTo(0, h * 0.72);
      ctx.bezierCurveTo(w * 0.15, h * 0.65, w * 0.30, h * 0.70, w * 0.45, h * 0.67);
      ctx.bezierCurveTo(w * 0.55, h * 0.65, w * 0.70, h * 0.69, w * 0.85, h * 0.66);
      ctx.bezierCurveTo(w * 0.92, h * 0.65, w * 0.98, h * 0.68, w, h * 0.67);
      ctx.lineTo(w, h * 0.75);
      ctx.lineTo(0, h * 0.75);
      ctx.closePath();
      ctx.fill();
    }

    function drawSeaReflection(w: number, h: number) {
      if (!ctx) return;
      // 海面渐变
      const seaGradient = ctx.createLinearGradient(0, h * 0.73, 0, h * 0.88);
      seaGradient.addColorStop(0, "rgba(60, 40, 80, 0.5)");
      seaGradient.addColorStop(0.3, "rgba(80, 50, 70, 0.4)");
      seaGradient.addColorStop(0.7, "rgba(50, 35, 60, 0.6)");
      seaGradient.addColorStop(1, "rgba(25, 18, 40, 0.8)");
      ctx.fillStyle = seaGradient;
      ctx.fillRect(0, h * 0.73, w, h * 0.15);

      // 太阳在水面的倒影光柱
      const reflectX = w * 0.5;
      const reflectGrad = ctx.createLinearGradient(reflectX - w * 0.08, h * 0.73, reflectX + w * 0.08, h * 0.88);
      reflectGrad.addColorStop(0, "rgba(255, 200, 100, 0.25)");
      reflectGrad.addColorStop(0.5, "rgba(255, 160, 60, 0.12)");
      reflectGrad.addColorStop(1, "rgba(240, 120, 40, 0)");
      ctx.fillStyle = reflectGrad;
      ctx.beginPath();
      ctx.moveTo(reflectX - w * 0.02, h * 0.73);
      ctx.lineTo(reflectX + w * 0.02, h * 0.73);
      ctx.lineTo(reflectX + w * 0.08, h * 0.88);
      ctx.lineTo(reflectX - w * 0.08, h * 0.88);
      ctx.closePath();
      ctx.fill();

      // 水面波纹线
      ctx.strokeStyle = "rgba(255, 200, 140, 0.08)";
      ctx.lineWidth = 1;
      for (let i = 0; i < 8; i++) {
        const y = h * 0.74 + i * (h * 0.018);
        ctx.beginPath();
        ctx.moveTo(0, y);
        for (let x = 0; x < w; x += 20) {
          const wave = Math.sin((x + time * 0.3 + i * 50) * 0.02) * 2;
          ctx.lineTo(x, y + wave);
        }
        ctx.stroke();
      }
    }

    function drawSandDunes(w: number, h: number) {
      if (!ctx) return;
      // 前景深色沙丘 / 地面
      const duneGrad = ctx.createLinearGradient(0, h * 0.85, 0, h);
      duneGrad.addColorStop(0, "rgba(20, 12, 30, 0.9)");
      duneGrad.addColorStop(0.5, "rgba(15, 8, 25, 0.95)");
      duneGrad.addColorStop(1, "rgba(10, 5, 18, 1)");

      ctx.fillStyle = duneGrad;
      ctx.beginPath();
      ctx.moveTo(0, h * 0.90);
      ctx.bezierCurveTo(w * 0.1, h * 0.86, w * 0.25, h * 0.88, w * 0.4, h * 0.87);
      ctx.bezierCurveTo(w * 0.55, h * 0.86, w * 0.7, h * 0.89, w * 0.85, h * 0.87);
      ctx.bezierCurveTo(w * 0.95, h * 0.86, w, h * 0.88, w, h * 0.88);
      ctx.lineTo(w, h);
      ctx.lineTo(0, h);
      ctx.closePath();
      ctx.fill();

      // 沙滩上的植被剪影
      drawBeachGrass(w, h);
    }

    function drawBeachGrass(w: number, h: number) {
      if (!ctx) return;
      const grassClusters = [
        { x: w * 0.05, y: h * 0.89, count: 5, height: 20 },
        { x: w * 0.15, y: h * 0.87, count: 4, height: 18 },
        { x: w * 0.82, y: h * 0.87, count: 6, height: 22 },
        { x: w * 0.92, y: h * 0.88, count: 3, height: 16 },
        { x: w * 0.35, y: h * 0.88, count: 4, height: 15 },
        { x: w * 0.60, y: h * 0.87, count: 5, height: 19 },
      ];

      ctx.strokeStyle = "rgba(15, 8, 25, 0.9)";
      ctx.lineWidth = 1.5;
      ctx.lineCap = "round";

      grassClusters.forEach((cluster) => {
        for (let i = 0; i < cluster.count; i++) {
          const offsetX = (i - cluster.count / 2) * 6;
          const bend = Math.sin(time * 0.01 + i) * 3;
          ctx.beginPath();
          ctx.moveTo(cluster.x + offsetX, cluster.y);
          ctx.quadraticCurveTo(
            cluster.x + offsetX + bend,
            cluster.y - cluster.height * 0.6,
            cluster.x + offsetX + bend * 1.5,
            cluster.y - cluster.height
          );
          ctx.stroke();
        }
      });
    }

    function drawLightParticles(w: number, h: number) {
      if (!ctx) return;
      // 浮动光点 — 模拟空气中漂浮的尘埃在落日下的反射
      const particles = 12;
      for (let i = 0; i < particles; i++) {
        const px = (w * ((i * 0.137 + 0.1) % 1));
        const py = (h * ((i * 0.213 + 0.05) % 0.7));
        const floatY = Math.sin(time * 0.008 + i * 1.5) * 8;
        const floatX = Math.cos(time * 0.006 + i * 2) * 5;
        const alpha = 0.15 + Math.sin(time * 0.01 + i) * 0.1;
        const size = 1.5 + Math.sin(time * 0.012 + i * 0.7) * 1;

        ctx.fillStyle = `rgba(255, 220, 160, ${alpha})`;
        ctx.beginPath();
        ctx.arc(px + floatX, py + floatY, size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    function animate() {
      time++;
      // 每 3 帧重绘一次，降低 CPU 占用
      if (time % 3 === 0) {
        draw();
      }
      animationId = requestAnimationFrame(animate);
    }

    resize();
    window.addEventListener("resize", resize);
    animate();

    return () => {
      window.removeEventListener("resize", resize);
      if (animationId) cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: -1,
      }}
    />
  );
}
